import { Ban, CheckCircle2, CreditCard, Eye, Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { normalizeSearch } from '@/lib/search';
import { cn, formatNumber } from '@/lib/utils';

import { OperadorDetalleDialogo } from '../components/operador-detalle-dialogo';
import { OperadorDialogo } from '../components/operador-dialogo';
import { useMutarOperadores, useOperadores } from '../configuracion.api';
import type { Operador } from '../configuracion.types';

const COLUMNAS =
  'grid grid-cols-[6rem_9rem_7rem_minmax(0,1fr)_7rem_11rem] items-center gap-x-3';

/** Mensaje de error legible para las mutaciones. */
function mensajeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return 'No se pudo completar la operación.';
}

/**
 * Operadores (`/configuracion/operadores`).
 *
 * Puerto de `Visa_operators` del panel anterior: alta, edición, activación y
 * baja de los operadores de pago, con el detalle de sus ventas y comisiones.
 */
export function OperadoresPage() {
  useDocumentTitle('Configuración · Operadores');

  const [buscar, setBuscar] = useState('');
  const [soloActivos, setSoloActivos] = useState(false);
  const [editando, setEditando] = useState<Operador | null>(null);
  const [creando, setCreando] = useState(false);
  const [viendo, setViendo] = useState<Operador | null>(null);
  const [porEliminar, setPorEliminar] = useState<Operador | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const operadores = useOperadores();
  const mutaciones = useMutarOperadores();

  const visibles = useMemo(() => {
    const termino = normalizeSearch(buscar.trim());
    const lista = operadores.data ?? [];

    return lista.filter((operador) => {
      if (soloActivos && !operador.activo) return false;
      if (!termino) return true;

      return [operador.nombre, operador.codigo, operador.descripcion]
        .map((valor) => normalizeSearch(valor ?? ''))
        .some((valor) => valor.includes(termino));
    });
  }, [operadores.data, buscar, soloActivos]);

  const activos = (operadores.data ?? []).filter((operador) => operador.activo).length;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Configuración</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <CreditCard className="size-6 text-brand-500" aria-hidden="true" />
            Operadores
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500">
            Operadores de pago con tarjeta y su porcentaje de comisión. Un operador con ventas
            asociadas no se puede eliminar: se desactiva.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void operadores.refetch()}
            loading={operadores.isFetching}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Actualizar
          </Button>
          <Button size="sm" onClick={() => setCreando(true)}>
            <Plus className="size-3.5" aria-hidden="true" />
            Nuevo operador
          </Button>
        </div>
      </header>

      {mutaciones.cambiarEstado.isError ? (
        <Alert variant="error" title="No se pudo cambiar el estado">
          {mensajeError(mutaciones.cambiarEstado.error)}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo eliminar el operador">
          {aviso}
        </Alert>
      ) : null}

      <SectionCard
        title="Operadores configurados"
        description={`${activos} activo(s) de ${(operadores.data ?? []).length}`}
        action={
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs font-medium text-ink-600">
              <input
                type="checkbox"
                checked={soloActivos}
                onChange={(event) => setSoloActivos(event.target.checked)}
                className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-200"
              />
              Sólo activos
            </label>

            <div className="w-56">
              <Input
                leftIcon={<Search className="size-4" aria-hidden="true" />}
                placeholder="Buscar por nombre o código…"
                value={buscar}
                onChange={(event) => setBuscar(event.target.value)}
              />
            </div>
          </div>
        }
      >
        {operadores.isPending ? (
          <Skeleton className="h-56 rounded-card" />
        ) : operadores.isError ? (
          <Alert variant="error" title="No se pudieron cargar los operadores">
            <div className="flex flex-wrap items-center gap-3">
              <span>{mensajeError(operadores.error)}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void operadores.refetch()}
                loading={operadores.isFetching}
              >
                Reintentar
              </Button>
            </div>
          </Alert>
        ) : visibles.length === 0 ? (
          <div className="rounded-control border border-dashed border-ink-200 px-4 py-12 text-center">
            <CreditCard className="mx-auto size-5 text-ink-300" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-ink-700">
              {buscar || soloActivos ? 'Ningún operador coincide con el filtro.' : 'Aún no hay operadores registrados.'}
            </p>
            {!buscar && !soloActivos ? (
              <Button size="sm" className="mt-3" onClick={() => setCreando(true)}>
                <Plus className="size-3.5" aria-hidden="true" />
                Crear el primero
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[58rem]">
              <div
                className={cn(
                  COLUMNAS,
                  'border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400',
                )}
              >
                <span>Código</span>
                <span>Nombre</span>
                <span className="text-right">Comisión</span>
                <span>Descripción</span>
                <span className="text-center">Estado</span>
                <span className="text-right">Acciones</span>
              </div>

              {visibles.map((operador) => (
                <div
                  key={operador.id}
                  className={cn(
                    COLUMNAS,
                    'border-b border-ink-50 py-2.5 text-sm text-ink-800',
                  )}
                >
                  <span>
                    <Badge variant="outline" title={operador.codigo}>
                      {operador.codigo}
                    </Badge>
                  </span>

                  <span className="truncate font-semibold text-ink-900" title={operador.nombre}>
                    {operador.nombre}
                  </span>

                  <span className="text-right tabular-nums text-amber-700">
                    {formatNumber(operador.comision)} %
                  </span>

                  <span className="truncate text-ink-500" title={operador.descripcion}>
                    {operador.descripcion || '—'}
                  </span>

                  <span className="flex justify-center">
                    <Badge variant={operador.activo ? 'success' : 'neutral'}>
                      {operador.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </span>

                  <span className="flex items-center justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Ver detalle"
                      aria-label={`Ver el detalle de ${operador.nombre}`}
                      onClick={() => setViendo(operador)}
                    >
                      <Eye className="size-4" aria-hidden="true" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Editar"
                      aria-label={`Editar ${operador.nombre}`}
                      onClick={() => setEditando(operador)}
                    >
                      <Pencil className="size-4" aria-hidden="true" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title={operador.activo ? 'Desactivar' : 'Activar'}
                      aria-label={`${operador.activo ? 'Desactivar' : 'Activar'} ${operador.nombre}`}
                      loading={
                        mutaciones.cambiarEstado.isPending &&
                        mutaciones.cambiarEstado.variables?.id === operador.id
                      }
                      onClick={() =>
                        mutaciones.cambiarEstado.mutate({
                          id: operador.id,
                          activo: !operador.activo,
                        })
                      }
                    >
                      {operador.activo ? (
                        <Ban className="size-4" aria-hidden="true" />
                      ) : (
                        <CheckCircle2 className="size-4" aria-hidden="true" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Eliminar"
                      aria-label={`Eliminar ${operador.nombre}`}
                      className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      onClick={() => {
                        setAviso(null);
                        setPorEliminar(operador);
                      }}
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </Button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {creando ? <OperadorDialogo operador={null} onClose={() => setCreando(false)} /> : null}

      {editando ? (
        <OperadorDialogo
          key={editando.id}
          operador={editando}
          onClose={() => setEditando(null)}
        />
      ) : null}

      {viendo ? (
        <OperadorDetalleDialogo
          key={viendo.id}
          operador={viendo}
          onClose={() => setViendo(null)}
        />
      ) : null}

      <ConfirmDialog
        open={porEliminar !== null}
        title={`Eliminar ${porEliminar?.nombre ?? ''}`}
        description="El operador se borra definitivamente. Si tiene ventas asociadas, la API lo rechazará y conviene desactivarlo."
        confirmLabel="Sí, eliminar"
        loading={mutaciones.eliminar.isPending}
        onConfirm={() => {
          if (!porEliminar) return;

          mutaciones.eliminar.mutate(porEliminar.id, {
            onError: (error) => setAviso(mensajeError(error)),
            onSettled: () => setPorEliminar(null),
          });
        }}
        onClose={() => setPorEliminar(null)}
      />
    </div>
  );
}
